'use client';
import React, { useState, useEffect, useRef } from 'react';
import AppNav from '@/components/AppNav';

import SafetyAndFooter from '@/components/SafetyAndFooter';
import { Conversation, Message, MemberProfile } from '@/lib/supabase/types';
import { jsnService } from '@/lib/supabase/jsnService';
import { createClient } from '@/lib/supabase/client';
import { MessageCircle, Send, Lock, Crown, ArrowLeft, Check, CheckCheck, Clock, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';
import { getPublicDisplayName } from '@/lib/supabase/nameUtils';

export default function MessagesScreen() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [photoStatus, setPhotoStatus] = useState<string | undefined>(undefined);
  const [isPhotoBlocked, setIsPhotoBlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [allProfiles, setAllProfiles] = useState<MemberProfile[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { window.location.href = '/sign-up-login-screen'; return; }

      // Email verification gate
      if (!user.email_confirmed_at) {
        window.location.href = '/verify-email';
        return;
      }

      const [up, mp] = await Promise.all([
        jsnService.getUserProfileById(user.id),
        jsnService.getCurrentMemberProfile(),
      ]);

      if (up?.accountStatus === 'suspended') { window.location.href = '/suspended'; return; }

      // ── Photo-review gate ──────────────────────────────────────────────
      // Block access until the profile photo is approved and account is active.
      // Admins bypass this gate. /discover-page renders the existing
      // "Photo Pending Review" UI when photoStatus is pending.
      const isAdmin = up?.role === 'admin';
      if (!isAdmin && (mp?.photoStatus !== 'approved' || up?.accountStatus !== 'active')) {
        window.location.href = '/discover-page';
        return;
      }
      // ──────────────────────────────────────────────────────────────────

      setCurrentUserId(user.id);
      setIsPremium(up?.membershipTier === 'premium' || false);

      const status = mp?.photoStatus;
      setPhotoStatus(status);
      setIsPhotoBlocked(status === 'pending' || status === 'rejected');

      const [convs, profiles] = await Promise.all([
        jsnService.getConversations(user.id),
        jsnService.getAllMemberProfiles(),
      ]);

      setConversations(convs);
      setAllProfiles(profiles);
      setIsLoading(false);
    };
    load();

    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (activeConvId && currentUserId) {
      jsnService.getMessages(activeConvId).then(msgs => {
        setMessages(msgs);
        jsnService.markConversationRead(activeConvId, currentUserId);
        jsnService.getConversations(currentUserId).then(setConversations);
      });
    }
  }, [activeConvId, currentUserId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getProfileForConv = (conv: Conversation) => {
    const otherId = conv.participantOneId === currentUserId ? conv.participantTwoId : conv.participantOneId;
    return allProfiles.find(p => p.userId === otherId);
  };

  const handleSelectConv = (convId: string) => {
    setActiveConvId(convId);
    if (isMobile) setShowChat(true);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !activeConvId || !currentUserId) return;
    if (!isPremium) {
      toast.info('Upgrade to Premium to send messages', {
        action: { label: 'Upgrade', onClick: () => { window.location.href = '/membership'; } },
      });
      return;
    }
    await jsnService.sendMessage(activeConvId, currentUserId, newMessage.trim());
    const msgs = await jsnService.getMessages(activeConvId);
    setMessages(msgs);
    const convs = await jsnService.getConversations(currentUserId);
    setConversations(convs);
    setNewMessage('');
  };

  const activeConv = conversations.find(c => c.id === activeConvId);
  const activeProfile = activeConv ? getProfileForConv(activeConv) : null;
  const totalUnread = conversations.reduce((sum, c) => {
    if (!currentUserId) return sum;
    return sum + (c.participantOneId === currentUserId ? c.unreadCountOne : c.unreadCountTwo);
  }, 0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-background text-foreground">
        <AppNav activePage="messages" />

        {isPhotoBlocked ? (
          <div className="pt-14 flex items-center justify-center min-h-screen px-4">
            <div className="max-w-md w-full text-center">
              {photoStatus === 'pending' ? (
                <div className="bg-[#FFBF00]/10 border border-[#FFBF00]/25 rounded-2xl p-8 space-y-4">
                  <div className="w-16 h-16 rounded-full bg-[#FFBF00]/15 flex items-center justify-center mx-auto">
                    <Clock size={28} style={{ color: '#FFBF00' }} />
                  </div>
                  <h2 className="text-xl font-bold text-foreground">Photo Pending Review</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Your profile photo is pending review. You&apos;ll be able to message members once your profile is approved.
                  </p>
                  <Link href="/account-settings" className="btn-primary px-6 py-3 rounded-xl text-sm font-semibold inline-block">
                    View Account Settings
                  </Link>
                </div>
              ) : (
                <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 space-y-4">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                    <AlertTriangle size={28} className="text-primary" />
                  </div>
                  <h2 className="text-xl font-bold text-foreground">Photo Rejected</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Your profile photo was rejected. Please upload a new clear profile photo for review.
                  </p>
                  <Link href="/discover-page" className="btn-primary px-6 py-3 rounded-xl text-sm font-semibold inline-block">
                    Upload New Photo
                  </Link>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="pt-14 h-[calc(100vh-56px)] flex">
            {/* Conversation List */}
            <div className={`${isMobile && showChat ? 'hidden' : 'flex'} md:flex flex-col w-full md:w-80 lg:w-96 border-r border-border bg-card flex-shrink-0`}>
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <h2 className="font-bold text-foreground">Messages</h2>
                  {totalUnread > 0 && (
                    <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-full font-bold">{totalUnread} new</span>
                  )}
                </div>
              </div>

              {conversations.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-3">
                  <MessageCircle size={32} className="text-muted-foreground" />
                  <p className="text-sm font-semibold text-foreground">No conversations yet</p>
                  <p className="text-xs text-muted-foreground">Like profiles and match to start messaging.</p>
                  <Link href="/discover-page" className="btn-primary px-4 py-2 rounded-lg text-xs font-semibold">
                    Browse Profiles
                  </Link>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto">
                  {conversations.map(conv => {
                    const profile = getProfileForConv(conv);
                    if (!profile) return null;
                    const unread = currentUserId
                      ? (conv.participantOneId === currentUserId ? conv.unreadCountOne : conv.unreadCountTwo)
                      : 0;
                    const convPublicName = getPublicDisplayName({ firstName: profile.firstName, lastName: profile.lastName, displayName: profile.displayName });
                    return (
                      <button
                        key={conv.id}
                        onClick={() => handleSelectConv(conv.id)}
                        className={`w-full flex items-center gap-3 p-4 border-b border-border/50 hover:bg-muted/20 transition-colors text-left ${
                          activeConvId === conv.id ? 'bg-primary/10 border-l-2 border-l-primary' : ''
                        }`}
                      >
                        <div className="relative flex-shrink-0">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm overflow-hidden"
                            style={{ background: `linear-gradient(135deg, ${profile.avatarColor}, ${profile.avatarColor}99)` }}>
                            {profile.photoUrl ? (
                              <img src={profile.photoUrl} alt={convPublicName} className="w-full h-full object-cover" />
                            ) : profile.avatarInitials}
                          </div>
                          {profile.profileStatus === 'online' && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-accent border-2 border-card" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="font-semibold text-foreground text-sm truncate">{convPublicName}</span>
                            {conv.lastMessageTime && (
                              <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                                {new Date(conv.lastMessageTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground truncate max-w-[160px]">
                              {isPremium ? (conv.lastMessage || 'Start a conversation') : '🔒 Upgrade to read messages'}
                            </p>
                            {unread > 0 && (
                              <span className="w-5 h-5 rounded-full bg-primary text-white text-[10px] flex items-center justify-center font-bold flex-shrink-0 ml-1">
                                {unread}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Chat Window */}
            <div className={`${isMobile && !showChat ? 'hidden' : 'flex'} md:flex flex-1 flex-col`}>
              {!activeConvId ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-4">
                  <div className="w-20 h-20 rounded-2xl bg-muted/20 flex items-center justify-center">
                    <MessageCircle size={36} className="text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-foreground">Select a conversation</h3>
                  <p className="text-sm text-muted-foreground max-w-xs">Choose a conversation from the list to start chatting.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 p-4 border-b border-border bg-card/95">
                    {isMobile && (
                      <button onClick={() => setShowChat(false)} className="p-1.5 text-muted-foreground hover:text-foreground">
                        <ArrowLeft size={18} />
                      </button>
                    )}
                    {activeProfile && (
                      <>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 overflow-hidden"
                          style={{ background: `linear-gradient(135deg, ${activeProfile.avatarColor}, ${activeProfile.avatarColor}99)` }}>
                          {activeProfile.photoUrl ? (
                            <img src={activeProfile.photoUrl} alt={getPublicDisplayName({ firstName: activeProfile.firstName, lastName: activeProfile.lastName, displayName: activeProfile.displayName })} className="w-full h-full object-cover" />
                          ) : activeProfile.avatarInitials}
                        </div>
                        <div>
                          <div className="font-semibold text-foreground text-sm">{getPublicDisplayName({ firstName: activeProfile.firstName, lastName: activeProfile.lastName, displayName: activeProfile.displayName })}</div>
                          <div className="text-xs text-muted-foreground">
                            {activeProfile.profileStatus === 'online' ? (
                              <span className="text-accent">● Online now</span>
                            ) : (
                              `${activeProfile.usState} · ${activeProfile.kenyanCounty}`
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {!isPremium && (
                    <div className="mx-4 mt-4 p-4 border border-primary/30 rounded-xl bg-primary/5 flex items-center gap-3">
                      <Crown size={20} className="text-primary flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-foreground">Premium required to read messages</p>
                        <p className="text-xs text-muted-foreground">Upgrade to read and send messages.</p>
                      </div>
                      <Link href="/membership" className="btn-primary px-3 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0">Upgrade</Link>
                    </div>
                  )}

                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.map(msg => {
                      const isOwn = msg.senderId === currentUserId;
                      return (
                        <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                            isOwn ? 'bg-primary text-white rounded-br-sm' : 'bg-muted/40 border border-border text-foreground rounded-bl-sm'
                          }`}>
                            {!isPremium && !isOwn ? (
                              <div className="flex items-center gap-1.5 text-sm">
                                <Lock size={12} className="text-muted-foreground" />
                                <span className="text-muted-foreground italic text-xs">Upgrade to read</span>
                              </div>
                            ) : (
                              <p className="text-sm">{msg.text}</p>
                            )}
                            <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? 'text-white/60' : 'text-muted-foreground'}`}>
                              <span className="text-[10px]">
                                {new Date(msg.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              {isOwn && (msg.isRead ? <CheckCheck size={11} /> : <Check size={11} />)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="p-4 border-t border-border bg-card/95">
                    {isPremium ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          className="input-jsn flex-1 py-2.5 text-sm"
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={e => setNewMessage(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        />
                        <button
                          onClick={handleSend}
                          disabled={!newMessage.trim()}
                          className="btn-primary p-2.5 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label="Send message"
                        >
                          <Send size={18} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-muted/20 rounded-xl border border-border">
                        <Lock size={16} className="text-muted-foreground flex-shrink-0" />
                        <p className="text-sm text-muted-foreground flex-1">Upgrade to Premium to send messages</p>
                        <Link href="/membership" className="btn-primary px-4 py-2 rounded-lg text-xs font-semibold flex-shrink-0">Upgrade</Link>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <SafetyAndFooter />
    </>
  );
}
